import { useEffect, useState } from "react";
import { LuCircleHelp, LuLoaderCircle, LuTriangleAlert } from "react-icons/lu";
import { BuildJobStatusSchema } from "../../../../darvin-shared/schema/zod/build";
import { useAuthUser } from "../../../core_stores/useAuthStore";
import {
  AndroidBuilds,
  BuildPlatform,
  useProjectBuildActions,
  useProjectBuildsAndroid,
  useProjectBuildsWeb,
  WebBuilds,
} from "../../../core_stores/useProjectBuildsStore";
import { colors, components, typography } from "../../../design/tokens";
import { HttpNotEnoughTokensException } from "../../../model/httpException";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { trackEvent } from "../../../utils/analyticsUtils";
import BuildProgressBar from "../../common_components/BuildProgressBar";

export default function ProjectPublishStartBuild({
  selectedPlatform,
}: {
  selectedPlatform: BuildPlatform;
}) {
  const buildsData: WebBuilds | AndroidBuilds | null =
    selectedPlatform === BuildPlatform.WEB
      ? useProjectBuildsWeb()
      : useProjectBuildsAndroid();
  const builds = buildsData?.builds;
  const user = useAuthUser();

  const { startWebBuild, startAndroidBuild } = useProjectBuildActions();
  const { showSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedBuildType, setSelectedBuildType] = useState<"apk" | "aab">(
    "apk"
  );
  const [canShowAabBuildWarning, setCanShowAabBuildWarning] = useState(false);

  const processingBuilds = buildsData?.builds?.filter(
    (build) =>
      build.status === BuildJobStatusSchema.enum.waiting ||
      build.status === BuildJobStatusSchema.enum.running
  );

  useEffect(() => {
    const shownAabBuildWarningUsers = JSON.parse(
      localStorage.getItem("shownAabBuildWarningUsers") ?? "[]"
    );
    if (!shownAabBuildWarningUsers.includes(user?.uid ?? "")) {
      setCanShowAabBuildWarning(true);
    }
  }, []);

  useEffect(() => {
    if (processingBuilds && processingBuilds.length > 0 && !isLoading) {
      setIsLoading(true);
    } else if (
      (!processingBuilds || processingBuilds.length === 0) &&
      isLoading
    ) {
      setIsLoading(false);
    }
  }, [buildsData?.builds]);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      if (selectedPlatform === BuildPlatform.WEB) {
        await startWebBuild();
        trackEvent("webpublish_started");
      } else if (selectedPlatform === BuildPlatform.ANDROID) {
        await startAndroidBuild(selectedBuildType);
        if (selectedBuildType === "apk") {
          trackEvent("apk_started");
        } else if (selectedBuildType === "aab") {
          trackEvent("aab_started");
        }

        if (selectedBuildType === "aab") {
          updateShownAabBuildWarningUsers();
          if (canShowAabBuildWarning) {
            setCanShowAabBuildWarning(false);
          }
        }
      }
    } catch (error) {
      if (error instanceof HttpNotEnoughTokensException) {
        showSnackbar("You don't have enough tokens to publish your app.", {
          type: "error",
        });
        setIsLoading(false);
      }
    }
  };

  const getDescription = () => {
    if (isLoading) {
      if (selectedPlatform === BuildPlatform.WEB) {
        return "Build in progress\nEstimated time 3-5 minutes.";
      } else if (selectedPlatform === BuildPlatform.ANDROID) {
        return "Build in progress\nEstimated time 10 minutes.\nYou can return here when it's ready.";
      }
    }
    if (selectedPlatform === BuildPlatform.WEB) {
      return "Publish as Progressive Web App (PWA)";
    } else if (selectedPlatform === BuildPlatform.ANDROID) {
      return "Build for Android";
    }
    return "";
  };

  const getButtonLabel = () => {
    if (isLoading) {
      if (selectedPlatform === BuildPlatform.WEB) {
        return (buildsData as WebBuilds)?.previewUrl
          ? "Updating..."
          : "Publishing...";
      } else if (selectedPlatform === BuildPlatform.ANDROID) {
        return "Generating...";
      }
    }
    if (selectedPlatform === BuildPlatform.WEB) {
      return (buildsData as WebBuilds)?.previewUrl ? "Update for free" : "Publish for free";
    } else if (selectedPlatform === BuildPlatform.ANDROID) {
      if (selectedBuildType === "apk") {
        return "Generate Build for 20K tokens";
      } else if (selectedBuildType === "aab") {
        return "Generate Build for 40K tokens";
      }
    }
    return "";
  };

  const updateShownAabBuildWarningUsers = () => {
    if (!user?.uid) return;

    const shownAabBuildWarningUsers = JSON.parse(
      localStorage.getItem("shownAabBuildWarningUsers") ?? "[]"
    );
    if (!shownAabBuildWarningUsers.includes(user?.uid)) {
      shownAabBuildWarningUsers.push(user?.uid);
      localStorage.setItem(
        "shownAabBuildWarningUsers",
        JSON.stringify(shownAabBuildWarningUsers)
      );
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center
    border rounded-xl border-dark-border
    px-4 md:px-6 py-[50px] gap-4 relative"
    >
      <span
        className={`max-w-[300px] text-center whitespace-pre-line ${typography.size.sm}`}
      >
        {getDescription()}
      </span>
      {selectedPlatform === BuildPlatform.ANDROID && !isLoading && (
        <AndroidBuildTypesSelector
          selectedBuildType={selectedBuildType}
          setSelectedBuildType={setSelectedBuildType}
        />
      )}
      {processingBuilds && processingBuilds.length > 0 ? (
        <BuildProgressBar
          buildCreationTimestamp={processingBuilds[0].creationTimestamp}
          estimatedBuildDurationInSeconds={
            selectedPlatform === BuildPlatform.WEB ? 300 : 600
          }
        />
      ) : (
        <Button
          id={`project-publish-start-build-${selectedPlatform}`}
          label={getButtonLabel()}
          isLoading={isLoading}
          isDisabled={isLoading || builds === null}
          handleClick={handleClick}
        />
      )}
      {selectedPlatform === BuildPlatform.WEB &&
        (buildsData as WebBuilds)?.previewUrl && <WebBuildTooltip />}
      {canShowAabBuildWarning &&
        !isLoading &&
        selectedPlatform === BuildPlatform.ANDROID &&
        selectedBuildType === "aab" && (
          <p className={`max-w-[500px] px-4 text-center ${typography.size.sm}`}>
            Google Play requires app icon to reflect the app's content. Builds
            with the default icon may not be approved. Please make sure you
            uploaded a custom one in App icon section before generating the AAB.
          </p>
        )}
    </div>
  );
}

function WebBuildTooltip() {
  return (
    <span
      className="absolute top-3 right-3"
      data-tooltip-id="dashboard-tooltip"
      data-tooltip-html={
        "Your app is currently published. Publishing it again will overwrite it."
      }
    >
      <LuCircleHelp />
    </span>
  );
}

function AabBuildTooltip() {
  return (
    <span
      className="text-dark-placeholder"
      data-tooltip-id="dashboard-tooltip"
      data-tooltip-html={
        "Google Play requires app icon to reflect the app's content. Builds with the default icon may not be approved. Please make sure you uploaded a custom one in App icon section before generating the AAB."
      }
    >
      <LuTriangleAlert />
    </span>
  );
}

function AndroidBuildTypesSelector({
  selectedBuildType,
  setSelectedBuildType,
}: {
  selectedBuildType: "apk" | "aab";
  setSelectedBuildType: (buildType: "apk" | "aab") => void;
}) {
  return (
    <div className="flex flex-col gap-3 px-4">
      <RadioBtn
        label="APK – Install directly on your device"
        isChecked={selectedBuildType === "apk"}
        onChange={() => setSelectedBuildType("apk")}
      />
      <div className="flex items-center gap-3">
        <RadioBtn
          label="AAB – Required for Google Play publishing"
          isChecked={selectedBuildType === "aab"}
          onChange={() => setSelectedBuildType("aab")}
        />
        <AabBuildTooltip />
      </div>
    </div>
  );
}

function RadioBtn({
  label,
  isChecked,
  onChange,
}: {
  label: string;
  isChecked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input
        type="radio"
        checked={isChecked}
        onChange={onChange}
        className={`
        peer relative appearance-none shrink-0 w-4 h-4 border-2 ${colors.border.primary}
        rounded-full bg-transparent
        focus:outline-none focus:ring-offset-0 focus:ring-0
        checked:bg-transparent checked:border-primary-blue
        disabled:border-steel-400 disabled:bg-steel-400
      `}
      />
      <div
        className="absolute w-[6px] h-[6px]
          pointer-events-none hidden
          peer-checked:block
          bg-primary-blue rounded-full
          translate-x-[5px]"
      />
      <span className={`text-sm ${colors.text.secondary}`}>{label}</span>
    </label>
  );
}

function Button({
  id,
  label,
  isLoading,
  isDisabled,
  handleClick,
}: {
  id: string;
  label: string;
  isLoading: boolean;
  isDisabled: boolean;
  handleClick: () => void;
}) {
  return (
    <button
      id={id}
      className={`${components.button.primary} w-full max-w-[250px] overflow-hidden`}
      onClick={handleClick}
      disabled={isDisabled}
    >
      {isLoading ? (
        <LuLoaderCircle className="animate-spin" size={20} />
      ) : (
        label
      )}
    </button>
  );
}
